import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSimulationForm } from '@/hooks/useSimulationForm';
import SimulationListPreview from '@/components/simulation/SimulationListPreview';
import DeleteSimulationModal from '@/components/simulation/DeleteSimulationModal';
import ConfirmDestructiveModal from '@/components/shared/ConfirmDestructiveModal';
import DifficultySelector from '@/components/simulation/DifficultySelector';
import FileUploadField from '@/components/simulation/FileUploadField';
import SalespersonSelect from '@/components/simulation/SalespersonSelect';
import CustomSelect from '@/components/ui/CustomSelect';
import { maestros, getMaestroName, getMaestroTitle } from '@/types/maestro';

export default function SimulationFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    form,
    simulations,
    salespeople,
    loading,
    submitting,
    error,
    editingId,
    existingDocs,
    pendingFiles,
    uploadingFiles,
    dragOver,
    rejectedUploads,
    limitReached,
    hasRejectedUploads,
    atDocumentLimit,
    dismissRejectedUpload,
    clearLimitReached,
    deleteTarget,
    showLeaveConfirm,
    salePhases,
    sectors,
    priceRanges,
    simulationTypeOptions,
    subSectors,
    watchSector,
    watchDifficulty,
    watchAssigned,
    resetForm,
    onSubmit,
    handleEdit,
    handleDrop,
    handleFileInput,
    removePendingFile,
    removeExistingDoc,
    handleBack,
    handleDeleted,
    setDeleteTarget,
    setShowLeaveConfirm,
    setDragOver,
  } = useSimulationForm();

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="animate-fade-in">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleBack(navigate)}
          className="mb-6 gap-1.5 text-muted-foreground"
        >
          <ArrowLeft className="size-4" />
          {t('configurator.backToDashboard')}
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading text-foreground">
            {t('configurator.pageTitle')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t('configurator.pageSubtitle')}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div role="alert" className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Simulation list */}
        <div className="mb-8">
          <h2 className="mb-4 text-lg font-semibold text-foreground">
            {t('configurator.simulationsCreated')}
          </h2>
          <SimulationListPreview
            simulations={simulations}
            loading={loading}
            onEdit={handleEdit}
            onDelete={(sim) => setDeleteTarget({ id: sim.id, name: sim.name })}
          />
        </div>

        {/* Form */}
        <div>
          <h2 id="simulation-form" className="mb-4 scroll-mt-20 text-lg font-semibold text-foreground">
            {editingId ? t('configurator.editSimulation') : t('configurator.newSimulation')}
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Language — primary editorial choice (drives catalog labels, briefing, evaluator).
                Locked in edit mode: changing it would invalidate maestro/briefing already linked to the sim. */}
            <Field
              label={t('configurator.languageLabel')}
              hint={t('configurator.languageHint')}
              error={errors.language?.message}
            >
              <CustomSelect
                value={watch('language') ?? 'es'}
                onChange={(val) => setValue('language', val as 'es' | 'it', { shouldDirty: true })}
                disabled={!!editingId}
                options={[
                  { value: 'es', label: t('configurator.languageEs') },
                  { value: 'it', label: t('configurator.languageIt') },
                ]}
              />
            </Field>

            {/* Documents — first so processing starts while the rest of the form is filled in */}
            <Field
              label={t('configurator.documentsLabel')}
              hint={t('configurator.uploadFirstLabel')}
            >
              <FileUploadField
                existingDocs={existingDocs}
                pendingFiles={pendingFiles}
                uploadingFiles={uploadingFiles}
                dragOver={dragOver}
                rejectedUploads={rejectedUploads}
                atLimit={atDocumentLimit}
                limitReached={limitReached}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onFileInput={handleFileInput}
                onRemovePending={removePendingFile}
                onRemoveExisting={removeExistingDoc}
                onDismissRejected={dismissRejectedUpload}
                onDismissLimitReached={clearLimitReached}
              />
            </Field>

            {/* Name + Assign salespeople */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('configurator.nameLabel')} error={errors.name?.message}>
                <Input {...register('name')} placeholder={t('configurator.namePlaceholder')} />
              </Field>

              {salespeople.length > 0 && (
                <Field label={t('configurator.assignSalespeopleLabel')} hint={t('configurator.assignSalespeopleHint')}>
                  <SalespersonSelect
                    salespeople={salespeople}
                    selected={watchAssigned}
                    onChange={(ids) => setValue('assignedSalespersonIds', ids)}
                  />
                </Field>
              )}
            </div>

            {/* Type + Phase */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('configurator.simulationTypeLabel')} error={errors.simulationType?.message}>
                <CustomSelect
                  value={watch('simulationType') ?? ''}
                  onChange={(val) => setValue('simulationType', val)}
                  placeholder={t('configurator.selectType')}
                  options={[
                    { value: '', label: t('configurator.selectType') },
                    ...simulationTypeOptions.map((o) => ({ value: o.value, label: o.label })),
                  ]}
                />
              </Field>
              <Field label={t('configurator.salePhaseLabel')} error={errors.salePhase?.message}>
                <CustomSelect
                  value={watch('salePhase') ?? ''}
                  onChange={(val) => setValue('salePhase', val)}
                  placeholder={t('configurator.selectPhase')}
                  options={[
                    { value: '', label: t('configurator.selectPhase') },
                    ...salePhases.map((p) => ({ value: p.value, label: p.label })),
                  ]}
                />
              </Field>
            </div>

            {/* Objections */}
            <Field label={t('configurator.objectionsLabel')}>
              <Textarea
                {...register('objections')}
                placeholder={t('configurator.objectionsPlaceholder')}
                rows={4}
                className="resize-none"
              />
            </Field>

            {/* Sector + Sub-sector */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('configurator.sectorLabel')}>
                <CustomSelect
                  value={watch('sector') ?? ''}
                  onChange={(val) => setValue('sector', val)}
                  placeholder={t('configurator.selectSector')}
                  options={[
                    { value: '', label: t('configurator.selectSector') },
                    ...sectors.map((s) => ({ value: s.value, label: s.label })),
                  ]}
                />
              </Field>
              <Field label={t('configurator.subSectorLabel')}>
                <CustomSelect
                  value={watch('subSector') ?? ''}
                  onChange={(val) => setValue('subSector', val)}
                  disabled={!watchSector}
                  placeholder={watchSector ? t('configurator.selectSubSector') : t('configurator.selectSectorFirst')}
                  options={[
                    { value: '', label: watchSector ? t('configurator.selectSubSector') : t('configurator.selectSectorFirst') },
                    ...subSectors.map((s) => ({ value: s.value, label: s.label })),
                  ]}
                />
              </Field>
            </div>

            {/* Price range + Maestro */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('configurator.priceRangeLabel')}>
                <CustomSelect
                  value={watch('priceRange') ?? ''}
                  onChange={(val) => setValue('priceRange', val)}
                  placeholder={t('configurator.selectPriceRange')}
                  options={[
                    { value: '', label: t('configurator.selectPriceRange') },
                    ...priceRanges.map((r) => ({ value: r.value, label: r.label })),
                  ]}
                />
              </Field>
              <Field label={`${t('configurator.assignedMaestroLabel')} (${t('common.optional', 'opcional')})`}>
                <CustomSelect
                  value={watch('assignedMaestroId') ?? ''}
                  onChange={(val) => setValue('assignedMaestroId', val)}
                  placeholder={t('configurator.maestroDefault')}
                  options={[
                    { value: '', label: t('configurator.maestroDefault') },
                    ...maestros.map((m) => ({ value: m.id, label: `${m.icon} ${getMaestroName(m)} — ${getMaestroTitle(m)}` })),
                  ]}
                />
              </Field>
            </div>

            {/* Difficulty */}
            <Field label={t('configurator.difficultyLabel')}>
              <DifficultySelector
                value={watchDifficulty}
                onChange={(level) => setValue('difficultyLevel', level)}
              />
            </Field>

            {/* Submit */}
            <div className="flex items-center gap-3 justify-end pt-2">
              <Button type="button" variant="ghost" onClick={resetForm}>
                {editingId ? t('configurator.cancelEdit') : t('configurator.clearFields')}
              </Button>
              <Tooltip open={hasRejectedUploads && !submitting ? undefined : false}>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button type="submit" disabled={submitting || hasRejectedUploads}>
                      {submitting ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : editingId ? (
                        <Pencil className="size-4" />
                      ) : (
                        <Plus className="size-4" />
                      )}
                      {editingId ? t('configurator.updateSimulation') : t('configurator.createSimulation')}
                    </Button>
                  </span>
                </TooltipTrigger>
                {hasRejectedUploads && !submitting && (
                  <TooltipContent>{t('configurator.submitBlockedRejected')}</TooltipContent>
                )}
              </Tooltip>
            </div>
          </form>
        </div>
      </div>

      {/* Delete modal */}
      <DeleteSimulationModal
        isOpen={!!deleteTarget}
        simulation={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />

      {/* Leave confirmation */}
      <ConfirmDestructiveModal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={() => navigate('/admin/simulations')}
        title={t('configurator.leaveTitle')}
        description={t('configurator.leaveMessage')}
        confirmLabel={t('configurator.leaveWithoutSaving')}
        cancelLabel={t('configurator.keepEditing')}
      />
    </TooltipProvider>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative rounded-xl border border-border bg-card p-4 sm:p-5 focus-within:z-10">
      <label className="mb-2 block text-sm font-semibold text-foreground">{label}</label>
      {hint && <p className="mb-3 text-xs text-muted-foreground">{hint}</p>}
      {children}
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}
